import { users } from '@prisma/client';
import pushSubscriptionsService from '../api/services/pushSubscriptions';

/**
 * Browser-side push subscription primitives, shared by the settings card and the
 * session-level reconciliation in AuthContext.
 *
 * The service worker itself is registered by the PWA shell
 * (`src/pwa/registerServiceWorker.ts`). This module deliberately talks to
 * `navigator.serviceWorker.ready` rather than importing that module, so it works with
 * whatever registered the worker. That promise never settles when no worker exists —
 * hence the timeout, which turns a silent hang into a real message.
 */

/** How long to wait for a service worker before reporting that the shell is missing. */
export const SERVICE_WORKER_READY_TIMEOUT_MS = 10_000;

export const isPushSupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const isStandaloneDisplay = (): boolean => {
  if (typeof window === 'undefined') return false;
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
    true;
  const displayModeStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  return iosStandalone || displayModeStandalone;
};

/** iOS/iPadOS, including iPadOS 13+ which reports a desktop Safari user agent. */
export const isIosDevice = (userAgent: string, maxTouchPoints = 0): boolean =>
  /iPad|iPhone|iPod/.test(userAgent) ||
  (/Macintosh/.test(userAgent) && maxTouchPoints > 1);

/** VAPID keys travel as base64url text; `PushManager.subscribe` wants raw bytes. */
export const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
};

export const serviceWorkerReady =
  async (): Promise<ServiceWorkerRegistration> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () =>
          reject(
            new Error(
              'The app is still starting up. Reload the page and try again.'
            )
          ),
        SERVICE_WORKER_READY_TIMEOUT_MS
      );
    });

    try {
      return await Promise.race([navigator.serviceWorker.ready, timeout]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

/**
 * Hand a browser subscription to the API. The server upserts on `endpoint`, so this
 * both registers a new device and rebinds an existing one to the current account.
 */
export const registerSubscription = async (
  subscription: PushSubscription
): Promise<void> => {
  const json = subscription.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('This browser returned an incomplete push subscription.');
  }
  await pushSubscriptionsService.create({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    userAgent: window.navigator.userAgent,
  });
};

/**
 * PBUYER accounts are the outside buyer on one of our seller's listings and never see a
 * Reva surface, so they can never own a subscription — the same rule the service's
 * `denyCounterparties` hook enforces.
 */
export const canOwnPushSubscription = (
  user: Pick<users, 'type'> | null | undefined
): boolean => !!user && user.type !== 'PBUYER';

/**
 * Runs an async worker on the latest enqueued item, never on two items at once.
 *
 * If `enqueue` is called again while the worker is still running, the in-flight call is
 * left to finish undisturbed and the new item replaces whatever was queued — an
 * intermediate item that gets superseded before its turn is simply dropped, never run.
 * Once the running call resolves, the queue immediately picks up the latest item, so a
 * burst of calls always ends with exactly one more run, on the most recent argument.
 *
 * Used to serialize `reconcilePushSubscription` across account changes on a shared
 * browser: without this, a fast sign-out/sign-in can let an earlier account's slower
 * upsert land on the server after a later account's faster one, leaving the endpoint
 * bound to the wrong person. Because everything here is synchronous except the single
 * `await worker(item)`, there is no window in which a call to `enqueue` can be missed.
 */
export const createSerialQueue = <T>(
  worker: (item: T) => Promise<unknown>
): { enqueue: (item: T) => void } => {
  let running = false;
  let queued: { item: T } | null = null;

  const drain = async () => {
    running = true;
    while (queued) {
      const { item } = queued;
      queued = null;
      await worker(item);
    }
    running = false;
  };

  return {
    enqueue: (item: T) => {
      queued = { item };
      if (!running) void drain();
    },
  };
};

export type PushReconcileResult = 'registered' | 'revoked' | 'failed' | 'none';

/**
 * Bind whatever push subscription this browser holds to the account that is signed in
 * now, or tear it down if that account may not own one.
 *
 * A browser subscription outlives the session and is shared by every account that signs
 * in on the profile. Without this, the row keeps its previous owner and their private
 * notifications keep arriving on a screen that now belongs to somebody else. It runs
 * from AuthContext on every session change, not only from the settings page, because
 * the person it protects is the one who never opens settings.
 *
 * Best-effort by design: it never throws and never blocks sign-in.
 */
export const reconcilePushSubscription = async (
  user: Pick<users, 'type'> | null | undefined
): Promise<PushReconcileResult> => {
  if (!isPushSupported()) return 'none';

  try {
    const registration = await serviceWorkerReady();
    const existing = await registration.pushManager.getSubscription();
    if (!existing) return 'none';

    if (!canOwnPushSubscription(user)) {
      // Revoke server-side first: if the browser-side unsubscribe fails we would
      // otherwise keep pushing to a device that must not receive it. Either half
      // succeeding stops delivery, so 'revoked' is only claimed when one of them
      // actually did — a silent double failure must not read as success.
      const serverRevoked = await pushSubscriptionsService
        .remove(existing.endpoint)
        .then(() => true)
        .catch(() => false);
      const browserRevoked = await existing.unsubscribe().catch(() => false);
      return serverRevoked || browserRevoked ? 'revoked' : 'failed';
    }

    await registerSubscription(existing);
    return 'registered';
  } catch {
    return 'none';
  }
};
